import { Position } from '../TableGridView';

export interface ColumnDragState {
    activeColumnId: string | null;
    columnIsOverColumn: string | null;
    activeColumnIndex: number | null;
    columnOverIndex: number | null;
    dragDistance: number;
}

interface GetColumnReorderStateProps {
    columnId: string;
    columnIndex: number;
    columnOrder: string[];
    columnDragState: ColumnDragState;
}

/**
 * Column-reordering is tricky because a column to the user isn't a single element - it's the <td> and all cells below it.
 *
 * Because of this, we need to update the UI (borders, paddings etc) of both <th> and the cells within said column. As
 * the row cells are _also_ in their own dnd context, we can't use the column context state directly and instead store it
 * in the columnDragState onDragEnd. That state gets interpreted into outcomes that this function returns to both <td> and <th>.
 */
export function getColumnReorderState({
    columnId,
    columnIndex,
    columnDragState,
}: Omit<GetColumnReorderStateProps, 'columnOrder'>) {
    const { activeColumnId, activeColumnIndex, columnOverIndex } = columnDragState;

    // Check if this column is the one being dragged
    const isInDraggedColumn = activeColumnId === columnId;

    // Check if we're in a sorting operation
    const isSorting =
        columnOverIndex !== activeColumnIndex &&
        columnOverIndex !== null &&
        activeColumnIndex !== null;

    // Check if the drag is over this column
    const isOverMe = isSorting && columnOverIndex === columnIndex;

    // Determine the insert position (before or after)
    const insertPosition =
        isSorting && activeColumnIndex !== null && columnOverIndex !== null
            ? columnOverIndex > activeColumnIndex
                ? Position.After
                : Position.Before
            : undefined;

    // Is any column being dragged
    const somethingIsDragged = activeColumnIndex !== null;

    // Is the dragged column different from this column
    const draggedColumnIsntMe = activeColumnIndex !== columnIndex;

    // Determine if the column will be dropped to the right
    const draggedColWillSnapOnMyRight =
        insertPosition === Position.After && columnOverIndex === columnIndex;

    // Determine if the column will be dropped to the left
    const draggedColWillSnapOnMyLeft =
        insertPosition === Position.Before && columnOverIndex === columnIndex + 1;

    // If another column is dragged, in a new position, and to my right, show border-r
    const isDraggedToMyRight =
        somethingIsDragged &&
        draggedColumnIsntMe &&
        (draggedColWillSnapOnMyRight || draggedColWillSnapOnMyLeft);

    // Check if the dragged column is on my right
    const draggedColumnIsOnMyRight = activeColumnIndex === columnIndex + 1;

    // Determine whether to show the right border
    const showBorderRight = isDraggedToMyRight || draggedColumnIsOnMyRight;

    // Determine whether to show the left border
    const showBorderLeft = (isInDraggedColumn && columnIndex === 0) || !isInDraggedColumn;

    return {
        isInDraggedColumn,
        isSorting,
        isOverMe,
        insertPosition,
        somethingIsDragged,
        draggedColumnIsntMe,
        isDraggedToMyRight,
        draggedColumnIsOnMyRight,
        showBorderRight,
        showBorderLeft,
    };
}
